/**
 * API client for INVI HQ scheduled reports. Mirrors the backend schemas in
 * backend/schemas.py (ScheduledReport*) and the /scheduled-reports router.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

export interface ReportTypeOption {
  key: string
  label: string
  description: string | null
  /** Cadence this report is built for. Preselected when the report is chosen. */
  default_frequency: string
}

export interface ScheduledReport {
  id: number
  report_type: string
  recipients: string[]
  frequency: string
  day_of_week: number | null
  day_of_month: number | null
  interval_minutes: number | null
  hour: number
  minute: number
  dealer_group_id: number | null
  is_enabled: boolean
  last_sent_at: string | null
  created_at: string
}

export interface ScheduledReportInput {
  report_type: string
  recipients: string[]
  frequency: string
  day_of_week?: number | null
  day_of_month?: number | null
  interval_minutes?: number | null
  hour: number
  minute: number
  is_enabled?: boolean
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(data?.detail || `Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export async function fetchReportTypes(): Promise<ReportTypeOption[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.reportTypes}`, {
    headers: authHeaders(),
  })
  return handleResponse<ReportTypeOption[]>(res)
}

export async function fetchScheduledReports(): Promise<ScheduledReport[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.base}`, {
    headers: authHeaders(),
  })
  return handleResponse<ScheduledReport[]>(res)
}

export async function createScheduledReport(input: ScheduledReportInput): Promise<ScheduledReport> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.base}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  })
  return handleResponse<ScheduledReport>(res)
}

export async function updateScheduledReport(
  id: number,
  patch: Partial<ScheduledReportInput>,
): Promise<ScheduledReport> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.byId(id)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  })
  return handleResponse<ScheduledReport>(res)
}

export async function deleteScheduledReport(id: number): Promise<void> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.byId(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}

/**
 * Sends the report immediately. Resolves to whether an email actually went out —
 * an Error Alert with no new failures runs successfully and mails nobody, and the
 * page says so rather than claiming it sent something.
 */
export async function runScheduledReportNow(id: number): Promise<boolean> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.scheduledReports.runNow(id)}`, {
    method: "POST",
    headers: authHeaders(),
  })
  const data = await handleResponse<{ status: string; sent: boolean }>(res)
  return data.sent
}
